import { Component, computed, effect, input, Input, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApexAxisChartSeries } from 'ng-apexcharts';
import { IInvestmentModelChartOut, IChartOptions } from 'src/models/charts.model';
import { ECurrencyCode } from 'src/models/common.model';
import { futureDevelopmentChartColors } from 'src/util/constants';

@Component({
  selector: 'liberro-future-development-chart',
  standalone: false,
  templateUrl: './liberro-future-development-chart.component.html',
  styleUrl: './liberro-future-development-chart.component.less'
})
export class LiberroFutureDevelopmentChartComponent {
  private translateService = inject(TranslateService);

  @Input({ required: true }) public currencyCode!: ECurrencyCode;
  public chartData = input.required<IInvestmentModelChartOut>();
  public totalYearsToInvest = input.required<number>();
  public hasErrored = input.required<boolean>();
  public chartOptions?: IChartOptions;
  public noDataToDisplay = computed(() => !this.shouldDisplayChart());

  public constructor() {
    effect(() => {
      if (this.chartData()) {
        this.updateChart();
      }
    });
  }

  private shouldDisplayChart(): boolean {
    const data = this.chartData();

    return !!(
      data.optimistic &&
      data.optimistic.length > 0 &&
      data.expected &&
      data.expected.length > 0 &&
      data.pessimistic &&
      data.pessimistic.length > 0 &&
      data.pureInvestment &&
      data.pureInvestment.length > 0
    );
  }

  public updateChart(): void {
    const data = this.chartData();
    if (!data || !this.shouldDisplayChart()) {
      return;
    }

    const series: ApexAxisChartSeries = [
      {
        type: 'line',
        data: data.optimistic.map(item => ({
          x: item.x,
          y: item.y,
        })),
        color: futureDevelopmentChartColors.optimistic,
      },
      {
        type: 'line',
        data: data.expected.map(item => ({
          x: item.x,
          y: item.y,
        })),
        color: futureDevelopmentChartColors.expected,
      },
      {
        type: 'line',
        data: data.pessimistic.map(item => ({
          x: item.x,
          y: item.y,
        })),
        color: futureDevelopmentChartColors.pessimistic,
      },
      {
        type: 'line',
        data: data.pureInvestment.map(item => ({
          x: item.x,
          y: item.y,
        })),
        color: futureDevelopmentChartColors.pureInvestment,
      },
    ];

    this.chartOptions = {
      series,
      chart: {
        height: 170,
        type: 'line',
        zoom: {
          enabled: false,
        },
        toolbar: {
          show: false,
        },
        sparkline: {
          enabled: false,
        },
      },
      stroke: {
        curve: 'straight',
        width: 2,
        colors: [
          futureDevelopmentChartColors.optimistic,
          futureDevelopmentChartColors.expected,
          futureDevelopmentChartColors.pessimistic,
          futureDevelopmentChartColors.pureInvestment,
        ],
      },
      fill: {
        type: 'solid',
        opacity: 1,
      },
      dataLabels: {
        enabled: false,
      },
      grid: {
        show: false,
        padding: {
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        categories: [],
        crosshairs: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          show: false,
        },
      },
      legend: {
        show: false,
      },
      markers: {
        size: 0,
      },
      tooltip: {
        enabled: false,
      },
    } as IChartOptions;
  }

  public getFinalValues(): { name: string; value: number; color: string }[] {
    const data = this.chartData();
    if (!data) return [];

    const getLastValue = (series: typeof data.optimistic) => {
      return series.length > 0 ? series[series.length - 1].y : 0;
    };

    return [
      {
        name: this.translateService.instant('newInvestment.settings.developmentModel.totalDeposit'),
        value: getLastValue(data.pureInvestment),
        color: futureDevelopmentChartColors.pureInvestment,
      },
      {
        name: this.translateService.instant('newInvestment.settings.developmentModel.expected'),
        value: getLastValue(data.expected),
        color: futureDevelopmentChartColors.expected,
      },
      {
        name: this.translateService.instant('newInvestment.settings.developmentModel.optimistic'),
        value: getLastValue(data.optimistic),
        color: futureDevelopmentChartColors.optimistic,
      },
      {
        name: this.translateService.instant('newInvestment.settings.developmentModel.pessimistic'),
        value: getLastValue(data.pessimistic),
        color: futureDevelopmentChartColors.pessimistic,
      },
    ];
  }
}
